import { createContext, useContext, useEffect, useState } from "react";
import {
	BrowserLogger,
	type LoggerMetadata,
	type LogEntry,
} from "./BrowserLogger";
import { randomUuidCompat } from "../../common/utils/randomUuidCompat";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";

import * as SentryReact from "@sentry/react";

dayjs.extend(timezone);

const LoggerContext = createContext<BrowserLogger | null>(null);

interface LoggerProviderProps {
	children: React.ReactNode;
}

function getNetworkType() {
	if ("connection" in navigator && navigator.connection) {
		const connection = navigator.connection as any;
		return connection.effectiveType || connection.type || "unknown";
	}
	return "unknown";
}

function createSentryOnLog():
	| ((entry: LogEntry, meta: LoggerMetadata) => void)
	| undefined {
	if (!import.meta.env.VITE_SENTRY_DSN) return undefined;

	return (entry: LogEntry, meta: LoggerMetadata) => {
		const attributes = meta as unknown as Record<string, unknown>;

		if (entry.level === "debug") {
			SentryReact.logger.debug(entry.msg, attributes);
		} else if (entry.level === "error") {
			SentryReact.logger.error(entry.msg, attributes);
		} else if (entry.level === "warn") {
			SentryReact.logger.warn(entry.msg, attributes);
		} else if (entry.level === "info") {
			SentryReact.logger.info(entry.msg, attributes);
		} else if (entry.level === "fatal") {
			SentryReact.logger.fatal(entry.msg, attributes);
		}
	};
}

export function LoggerProvider({ children }: LoggerProviderProps) {
	const [sessionId] = useState(() => randomUuidCompat());
	const [logger] = useState(
		() =>
			new BrowserLogger({
				sessionId,
				source: "frontend",
				metadata: {
					appPlatform:
						import.meta.env.VITE_CAPACITOR_BUILD === "true"
							? "mobile"
							: "browser",
					appVersion: import.meta.env.VITE_APP_VERSION || "unknown",
					userAgent: navigator.userAgent || "unknown",
					screenResolution: `${window.innerWidth}x${window.innerHeight}`,
					timezone: dayjs.tz.guess() || "unknown",
					networkType: getNetworkType(),
				},
				enableConsoleLog: import.meta.env.DEV,
				minLevel: "info",
				enabled: import.meta.env.VITE_LOGGER_ENABLED === "true",
				onLog: createSentryOnLog(),
				flush: () => SentryReact.flush(2000),
			}),
	);

	useEffect(() => {
		if (import.meta.env.VITE_CAPACITOR_BUILD === "true") {
			import("@capacitor/device").then(({ Device }) => {
				Device.getInfo()
					.then((info) => {
						logger.setDeviceInfo(info);
					})
					.catch((err) =>
						logger.error(err?.message || "Error getting device info"),
					);
			});
		}
	}, [logger]);

	return (
		<LoggerContext.Provider value={logger}>{children}</LoggerContext.Provider>
	);
}

export function useLogger() {
	const context = useContext(LoggerContext);
	if (!context) {
		throw new Error("useLogger must be used within a LoggerProvider");
	}
	return context;
}
